import { runInAction, when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CustomField, ICustomField, Person } from '@feathr/blackbox';
import { FieldCollection, FieldDataType } from '@feathr/blackbox';
import { AsyncSelect, ButtonValid, Fieldset, Form, Input } from '@feathr/components';
import DataTypeSelect from '@feathr/extender/components/DataTypeSelect';
import { FieldOption } from '@feathr/extender/components/SelectOptions';
import { StoresContext, useAccount } from '@feathr/extender/state';
import { moment, TimeFormat } from '@feathr/hooks';

interface IProps {
  toggleAddData: () => void;
  person: Person;
}

interface IButtonProps {
  customField: CustomField;
  onSave: () => void;
}

const typeToValueMap = {
  [FieldDataType.date]: moment.utc().format(TimeFormat.isoDateTime),
  [FieldDataType.str]: '',
  [FieldDataType.bool]: true,
  [FieldDataType.float]: 1,
  [FieldDataType.int]: 1,
  [FieldDataType.list]: [],
};

const SaveButton = observer(({ customField, onSave }: IButtonProps) => {
  const { t } = useTranslation();

  const validation = customField.validate(['u_key', 'data_type'], false);
  return (
    <ButtonValid errors={validation.errors} onClick={onSave}>
      {t('Create')}
    </ButtonValid>
  );
});

function PersonAddCustomData({ person, toggleAddData }: IProps): JSX.Element {
  const account = useAccount();
  const { CustomFields } = useContext(StoresContext);
  const [newField, setNewField] = useState<CustomField | undefined>(undefined);
  const { t } = useTranslation();

  const createNewField = async (): Promise<void> => {
    if (newField) {
      await CustomFields.add(newField);
      runInAction(() => {
        customFields.models.push(newField);
      });
      const value = typeToValueMap[newField.get('data_type')];
      person.set({
        custom_data: {
          ...person.get('custom_data', {}),
          [newField.get('u_key')]: value,
        },
      });
      setNewField(undefined);
    }
    toggleAddData();
  };

  const customFields = CustomFields.list({
    filters: { collection: FieldCollection.Person, is_archived__ne: true },
    pagination: { page_size: 20 },
  });

  const loadOptions = async (inputValue: string) => {
    const fields = CustomFields.list({
      filters: {
        collection: FieldCollection.Person,
        is_archived__ne: true,
        u_key__icontains: inputValue,
      },
      pagination: {
        page_size: 20,
      },
    });
    await when(() => !fields.isPending);
    if (account.isFalcon) {
      return [{ id: 'create', u_key: 'Create New Field' }, ...fields.models.map((cf) => cf.toJS())];
    }
    return fields.models.map((cf) => cf.toJS());
  };

  const onSelectSingle = (option: ICustomField) => {
    if (option.id === 'create') {
      const field = CustomFields.create({ u_key: '', collection: FieldCollection.Person });
      setNewField(field);
    } else {
      person.set({
        custom_data: {
          ...person.get('custom_data'),
          [option.u_key]: typeToValueMap[option.data_type],
        },
      });
      toggleAddData();
    }
  };

  function handleChangeDataType(newValue?: FieldDataType) {
    newField!.set({ data_type: newValue });
  }

  const NewFieldInput = (
    <>
      <Input
        attribute={'u_key'}
        model={newField!}
        placeholder={t('Custom field name')}
        type={'text'}
      />
      {!!newField && newField.get('u_key').length ? (
        <DataTypeSelect onChange={handleChangeDataType} placeholder={t('Select data type...')} />
      ) : null}
      {!!newField && <SaveButton customField={newField} onSave={createNewField} />}
    </>
  );

  return (
    <Form label={t('Add custom data')}>
      <Fieldset>
        {!newField ? (
          <AsyncSelect<ICustomField>
            components={{ Option: FieldOption }}
            defaultOptions={customFields.models.map((cf) => cf.toJS())}
            getOptionLabel={(option) => option.u_key}
            getOptionValue={(option) => option.id!}
            isLoading={customFields.isPending}
            key={'customField'}
            label={t('Custom Field')}
            loadOptions={loadOptions}
            onSelectSingle={onSelectSingle}
          />
        ) : (
          NewFieldInput
        )}
      </Fieldset>
    </Form>
  );
}

export default observer(PersonAddCustomData);
